import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import queryString from 'query-string';
import {Location} from '@reach/router';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import {usePhaseState} from 'funnel-schedule/phase-context';
import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import Footer from 'components/Elements/Footer';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import Video from 'components/Video';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import useBalanceText from 'utils/useBalanceText';

import SectionCL from 'components/ComponentLibrary/SectionCL';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  noShare = false,
  noFixedVideo = false,
  noExitIntent = false,
  slug = 'masterclass',
  navigate,
  variant = 'default',
  ...props
}) {
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        // @ts-ignore
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();

  function Logo({className = null, src = 'https://cdn.foodrevolution.org/hhc/hhc-masterclass-logo.svg'}) {
    return <img src={src} alt="healthy heart masterclass logo" style={{maxHeight: '90px'}} className={className} />;
  }
  const ctaButtonCopy = (
    <>
      Reserve Your <br className="d-none d-lg-block d-xl-none" />
      <i>Free</i> Spot Now
    </>
  );
  const ctaButton = <OptinModalTrigger>{ctaButtonCopy}</OptinModalTrigger>;

  const headerImageUrl = 'https://cdn.foodrevolution.org/hhc/images/1166044116.jpg';

  useBalanceText();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta />
      <div className="page-content">
        <Header bgColor="white" textColor="black" adLockup={variant === 'ad' ? true : false} />
        <SectionCL
          id="hhc-header-poster"
          color="black"
          imgUrl={headerImageUrl}
          angle="none"
          type="triangle"
          className={classNames(`text-white`, `hhc-header-image-control`)}
          innerWrapClass="d-flex flex-column flex-grow-1"
          innerClass="d-flex flex-column flex-grow-1"
          imgBgClass="image-control"
        />
        <SectionCL
          id="hhc-header-poster-optin"
          color="white"
          className="text-center"
          type="triangle"
          angle="top"
          innerClass="sectionCL-padding-bottom"
        >
          <Container>
            <Row>
              <Col>
                <div className="optin-box background-white box-shadow-black-50 rounded p-4 p-lg-5 mb-5">
                  <p className="text-center balance-text mb-4">
                    <i>
                      <b>FREE Masterclass Now Playing For A Limited Time!</b>
                    </i>
                  </p>
                  <Logo className="mb-4" />
                  <p className="balance-text text-h3 mb-4">
                    At Last: A Science-Backed Approach to Achieving and Maintaining a Healthy Heart
                  </p>
                  <div className="mb-4">{ctaButton}</div>
                </div>
                <p className="text-h4 mb-0">
                  Learn the science behind lasting heart health with award-winning holistic cardiologist Dr. Mimi
                  Guarneri in the <i>Healthy Heart Masterclass.</i>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="green" className="text-center text-white" type="triangle" angle="both">
          <Container>
            <Row>
              <Col>
                <h4 className="section-only-heading">In This 90-Minute Masterclass You Will:</h4>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="white" type="triangle">
          <Container>
            <Row>
              <Col>
                <IconListCL>
                  <>
                    <h5 className="mt-0 mb-1 text-green">Discover the four pervasive myths about heart health</h5>
                    <p className="mb-0">
                      {
                        {
                          default: (
                            <>
                              that many doctors still believe (hint: they’re why heart disease is still the #1 killer in
                              the world, despite amazing medical advances)
                            </>
                          ),
                          ad: <>that many doctors still believe</>
                        }[variant]
                      }
                    </p>
                  </>
                  <>
                    <h5 className="mt-0 mb-1 text-green">Cut through the hype and controversies</h5>
                    <p className="mb-0">
                      {
                        {
                          default: <>and learn which foods heal — and which harm — your heart</>,
                          ad: <>and learn which foods heal — and which harm — the heart</>
                        }[variant]
                      }
                    </p>
                  </>
                  <>
                    <h5 className="mt-0 mb-1 text-green">Learn a 2-minute daily routine</h5>
                    <p className="mb-0">
                      {
                        {
                          default: (
                            <>
                              Find out how a simple 2-minute daily dental hygiene routine can significantly cut your
                              risk of heart disease
                            </>
                          ),
                          ad: (
                            <>
                              Find out how a simple 2-minute daily dental hygiene routine can significantly improve
                              heart health
                            </>
                          )
                        }[variant]
                      }
                    </p>
                  </>
                  {variant === 'default' && (
                    <>
                      <h5 className="mt-0 mb-1 text-green">Find out if you are at risk</h5>
                      <p className="mb-0">
                        Understand how a condition that you may not even realize you have can really damage your heart
                        in the long run — and how to find out whether you’re at risk
                      </p>
                    </>
                  )}
                  <>
                    <h5 className="mt-0 mb-1 text-green">Discover the single best exercise for your heart</h5>
                    <p className="mb-0">
                      {
                        {
                          default: (
                            <>
                              that doesn’t require a gym membership, weights, or even a lot of room — that you’ll enjoy
                              so much, you won’t have to “force yourself” to do it
                            </>
                          ),
                          ad: <>that doesn’t require a gym membership, weights, or even a lot of room</>
                        }[variant]
                      }
                    </p>
                  </>
                  <>
                    <h5 className="mt-0 mb-1 text-green">Learn how your emotions impact your health</h5>
                    <p className="mb-0">
                      {
                        {
                          default: <>like your blood pressure and even your cholesterol</>,
                          ad: <>like blood pressure and even cholesterol</>
                        }[variant]
                      }
                    </p>
                  </>
                  <>
                    <h5 className="mt-0 mb-1 text-green">Get introduced to a simple breathing technique</h5>
                    <p className="mb-0">
                      {
                        {
                          default: <>that can instantly reduce stress and help you feel more peaceful</>,
                          ad: <>that can instantly reduce stress and bring peace</>
                        }[variant]
                      }
                    </p>
                  </>
                  <>
                    <h5 className="mt-0 mb-1 text-green">Avoid the number one mistake</h5>
                    <p className="mb-0">that stops people from making progress when they start a new health program</p>
                  </>
                </IconListCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="green" className="text-center text-white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">Meet Your Instructor</h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id=""
          color="white"
          type="triangle"
          imgUrl="https://cdn.foodrevolution.org/hhc/images/mimi-guarneri-headshot.jpg"
          imgPosition="left"
          collapseHeader={
            <>
              <h5 className="text-400 m-0">Click Here to</h5>
              <h4 className="m-0">Learn More About Dr. Mimi Guarneri</h4>
            </>
          }
          collapseHeadlines={
            <>
              <h3 className="section-heading mb-2">Dr. Mimi Guarneri</h3>
              <h5 className="text-400 mt-0 mb-0">
                Dr. Mimi Guarneri is one of the top holistic cardiologists on the planet.
              </h5>
            </>
          }
          collapseContent={
            <>
              <p>
                You may recognize Holistic Cardiologist Dr. Mimi Guarneri from many globally-syndicated television shows
                including <i>NBC Today,</i> <i>PBS,</i> and <i>The Dr. Oz Show.</i>
              </p>
              <p className="balance-text">
                Dr. Guarneri’s passion for heart health was born after tragedy struck when her mother died of heart
                issues at age 39, and her father at age 50. Today, she’s an award-winning cardiac specialist who’s
                guided millions of people to uncover holistic solutions that are backed by the leading edge of medical
                research. What she shares with you could make all the difference.
              </p>
            </>
          }
        />
        <SectionCL id="hhc-host" color="green" className="text-center text-white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">Meet Your Host</h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id=""
          color="white"
          type="triangle"
          imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
          imgPosition="right"
          collapseHeader={
            <>
              <h5 className="text-400 m-0">Click Here to</h5>
              <h4 className="m-0">Learn More About Ocean Robbins</h4>
            </>
          }
          collapseHeadlines={
            <>
              <h3 className="section-heading mb-2">Ocean Robbins</h3>
              <h5 className="text-400 mt-0 mb-0">A food revoluton leader and inspiration to millions.</h5>
            </>
          }
          collapseContent={
            <>
              <p className="balance-text">
                Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
                best-selling author, husband, father, gardener, and dancer. Born in a log cabin and raised on home-grown
                food, his work is rooted in his family’s health-conscious legacy. From founding the nonprofit YES! at
                age 16 to leading a global food revolution movement, Ocean is on a mission to transform our food system,
                drive down the chronic disease epidemic, and mobilize people worldwide to be part of the solution for a
                healthier planet and population. His books include <i>31-Day Food Revolution</i> and{' '}
                <i>Powered By Plants.</i>
              </p>
            </>
          }
        />
        <SectionCL
          id="hhc-final-cta"
          color="light-gray"
          className="text-center"
          type="triangle"
          angle="none"
          order="last"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-white box-shadow-black-50 rounded p-4 p-lg-5">
                  <h2 className="section-heading text-green mb-2">
                    <i>Don’t Miss Your Chance!</i>
                  </h2>
                  <h3 className="m-0">For a limited time watch the</h3>
                  <Logo className="my-5" />
                  <div>{ctaButton}</div>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>

      <div className="footer-content mt-auto">
        <Footer adVariant={variant === 'ad' ? true : false} />
      </div>

      <OptinModal variant={variant} />
      {noExitIntent ? null : <ModalExitLanding />}
    </div>
  );
}
